import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import packageJson from '../../package.json';
import AuthLayout from 'layouts/AuthLayout';
import { getOnChangeFunction, getFormattedMissingFieldsMessage } from 'utils';
import { useAuth } from 'auth/AuthContext';
import { makeRequest } from 'services/request.service';
import { showAlert } from 'services/alerts.service';

const Login = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');

  const onChangeValue = getOnChangeFunction(setCredentials, credentials);

  const onSubmit = async e => {
    e.preventDefault();

    const htmlMessage = getFormattedMissingFieldsMessage(credentials, ['email', 'password']);
    if (htmlMessage !== '') {
      showAlert({
        title: t('auth.missingFields'),
        html: htmlMessage,
        icon: 'warning'
      });
      return;
    }

    await loginRequest();
  };

  const loginRequest = async () => {
    try {
      const result = await makeRequest({
        method: 'post',
        url: '/login',
        data: credentials
      });

      if (!result || !result.data) {
        throw new Error('No data returned from server');
      }
      if (result.status === 200) {
        login(result.data);
        navigate('/dashboard/command-center');
      }
    } catch (err) {
      console.error('Login Error:', err);

      if (err.response) {
        const { status } = err.response;
        if (status === 401 || status === 403) {
          setError(t('auth.loginError'));
          return;
        }
      }
      setError(t('auth.tryAgain'));
    }
  };

  return (
    <AuthLayout>
      <h2 className="font-command-display text-2xl font-bold text-greenPrimary mb-1">
        {t('auth.loginTitle')}
      </h2>
      <p className="auth-subtitle font-command-label text-xs uppercase tracking-widest mb-8">
        {t('auth.signIn')}
      </p>

      <form className="w-full" onSubmit={onSubmit} autoComplete="off">
        <div className="mb-5">
          <label className="auth-label block font-command-label text-xs uppercase tracking-widest mb-1.5">
            {t('auth.email')}
          </label>
          <input
            type="email"
            name="email"
            placeholder="you@example.com"
            value={credentials.email}
            onChange={onChangeValue}
            className="auth-input w-full px-3 py-2.5 border rounded-md text-sm transition"
          />
        </div>

        <div className="mb-7">
          <div className="flex items-center justify-between mb-1.5">
            <label className="auth-label block font-command-label text-xs uppercase tracking-widest">
              {t('auth.password')}
            </label>
            {/* <a
              href="#forgot"
              className="auth-link font-command-label text-xs hover:underline"
              onClick={e => e.preventDefault()}
            >
              {t('auth.forgotPassword')}
            </a> */}
          </div>
          <input
            type="password"
            name="password"
            placeholder="••••••••"
            value={credentials.password}
            onChange={onChangeValue}
            className="auth-input w-full px-3 py-2.5 border rounded-md text-sm transition"
          />
        </div>

        <button
          type="submit"
          className="workspace-primary-action w-full font-command-label text-xs uppercase tracking-widest py-2.5 rounded-md transition"
        >
          {t('auth.loginButton')}
        </button>

        {error && <p className="auth-error text-sm mt-3 text-center">{error}</p>}
      </form>

      <div className="auth-muted text-center mt-6 text-sm">
        {t('auth.noAccount')}{' '}
        <a href="/signup" className="auth-link font-semibold hover:underline">
          {t('auth.signUpHere')}
        </a>
      </div>

      <p className="auth-subtitle font-command-label text-xs text-center mt-8 uppercase tracking-widest">
        v{packageJson.version}
      </p>
    </AuthLayout>
  );
};

export default Login;
